/**
 * Agents Resource
 *
 * Thin 1:1 mapping to the `/v1/agents` API. An Agent is a named, account-scoped
 * instance of a persona; it carries no configuration of its own.
 */

import type { HttpLayer } from '../http.js';
import type {
  Agent,
  AgentDeleteResult,
  AgentSessionPage,
  AgentTeamEntry,
  AgentWorkspaceEntry,
  CreateAgentRequest,
  ListAccountAgentsRequest,
  ListAgentSessionsRequest,
  Session,
  StartAgentSessionRequest,
  UpdateAgentRequest,
} from '../types/index.js';

export class AgentsResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/agents';

  /**
   * List the account's Agents, every persona's default Agent included.
   * Deleted Agents are not listed.
   * @param params Optional `persona_id` to list only that persona's Agents.
   */
  async list(params?: ListAccountAgentsRequest): Promise<Agent[]> {
    return this.http.get(this.prefix, params?.persona_id ? { persona_id: params.persona_id } : undefined);
  }

  /**
   * Get one Agent by id. A deleted Agent is still returned, with `deleted_at` set.
   * @param agentId The Agent id.
   */
  async get(agentId: string): Promise<Agent> {
    return this.http.get(`${this.prefix}/${agentId}`);
  }

  /**
   * Create an Agent that runs a persona. The Agent has no configuration of its own:
   * it always runs its persona's current configuration. A deleted persona cannot
   * back a new Agent. Names are unique among the account's Agents.
   * @param params `persona_id`, `name`, and an optional `description`.
   */
  async create(params: CreateAgentRequest): Promise<Agent> {
    return this.http.post(this.prefix, params);
  }

  /**
   * Rename an Agent or change its description.
   * @param agentId The Agent id.
   * @param params `name` and/or `description`.
   */
  async update(agentId: string, params: UpdateAgentRequest): Promise<Agent> {
    return this.http.patch(`${this.prefix}/${agentId}`, params);
  }

  /**
   * Delete an Agent. The delete is soft: its sessions and history stay, it can no
   * longer start or join work, and its active work is stopped with a reason naming
   * who deleted it. The delete is recorded in the account's audit trail.
   * @param agentId The Agent id.
   */
  async delete(agentId: string): Promise<AgentDeleteResult> {
    return this.http.delete(`${this.prefix}/${agentId}`);
  }

  /**
   * One page of the sessions an Agent has participated in, as the primary agent or a
   * subagent, active and past, most recently active first. Follow `next_cursor` for
   * older sessions.
   * @param agentId The Agent id.
   * @param params Optional `limit` (1 to 100, default 50) and the previous page's `cursor`.
   */
  async sessions(agentId: string, params?: ListAgentSessionsRequest): Promise<AgentSessionPage> {
    return this.http.get(`${this.prefix}/${agentId}/sessions`, params);
  }

  /**
   * The distinct workspaces of the sessions an Agent has participated in.
   * @param agentId The Agent id.
   */
  async workspaces(agentId: string): Promise<AgentWorkspaceEntry[]> {
    return this.http.get(`${this.prefix}/${agentId}/workspaces`);
  }

  /**
   * The work-management teams of the org-chart positions an Agent staffs.
   * @param agentId The Agent id.
   */
  async teams(agentId: string): Promise<AgentTeamEntry[]> {
    return this.http.get(`${this.prefix}/${agentId}/teams`);
  }

  /**
   * Start a new chat whose primary agent is this Agent. A deleted Agent, or one
   * whose persona was deleted, cannot start a chat.
   * @param agentId The Agent id.
   * @param params Optional `name` and `workspace_id` (omitted: a workspace-less chat).
   */
  async startSession(agentId: string, params?: StartAgentSessionRequest): Promise<Session> {
    return this.http.post(`${this.prefix}/${agentId}/sessions`, params ?? {});
  }
}
